import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { useProducts } from '../context/ProductContext';
import { FilterIcon, SlidersIcon, XIcon, ChevronDownIcon } from 'lucide-react';
export const ProductListing: React.FC = () => {
  const {
    categoryName
  } = useParams<{
    categoryName: string;
  }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const {
    products,
    categories,
    getProductsByCategory,
    searchProducts
  } = useProducts();
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState('featured');
  const category = categories.find(c => c.id === categoryName);
  useEffect(() => {
    let result = products;
    // Apply category filter if specified
    if (categoryName && categoryName !== 'all') {
      result = getProductsByCategory(categoryName);
      setSelectedCategory(categoryName);
    } else {
      setSelectedCategory(null);
    }
    // Apply search filter if specified
    if (searchQuery) {
      result = searchProducts(searchQuery);
    }
    // Apply price filter
    result = result.filter(product => product.price >= priceRange[0] && product.price <= priceRange[1]);
    // Apply sorting
    switch (sortOption) {
      case 'price-low':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        // In a real app, we'd sort by date
        break;
      case 'rating':
        result = [...result].sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // 'featured' - no additional sorting
        break;
    }
    setFilteredProducts(result);
  }, [categoryName, searchQuery, products, getProductsByCategory, searchProducts, priceRange, sortOption]);
  return <div className="container mx-auto px-4 py-8" data-id="element-1347">
      {/* Header */}
      <div className="mb-8" data-id="element-1348">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1349">
          {searchQuery ? `Search Results for "${searchQuery}"` : category ? category.name : 'All Products'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2" data-id="element-1350">
          {filteredProducts.length} products found
        </p>
      </div>
      {/* Filter and Sort Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-1351">
        <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1352">
          <FilterIcon size={18} className="mr-2" data-id="element-1353" />
          Filter Results
        </button>
        <div className="flex items-center" data-id="element-1354">
          <label htmlFor="sort" className="mr-2 text-gray-700 dark:text-gray-300" data-id="element-1355">
            Sort by:
          </label>
          <select id="sort" value={sortOption} onChange={e => setSortOption(e.target.value)} className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-1356">
            <option value="featured" data-id="element-1357">Featured</option>
            <option value="price-low" data-id="element-1358">Price: Low to High</option>
            <option value="price-high" data-id="element-1359">Price: High to Low</option>
            <option value="newest" data-id="element-1360">Newest First</option>
            <option value="rating" data-id="element-1361">Best Rating</option>
          </select>
        </div>
      </div>
      {/* Filter Panel */}
      {isFilterOpen && <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6" data-id="element-1362">
          <div className="flex justify-between items-center mb-4" data-id="element-1363">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center" data-id="element-1364">
              <SlidersIcon size={18} className="mr-2" data-id="element-1365" />
              Advanced Filters
            </h3>
            <button onClick={() => setIsFilterOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-1366">
              <XIcon size={18} data-id="element-1367" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1368">
            {/* Price Range Filter */}
            <div data-id="element-1369">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1370">
                Price Range
              </label>
              <div className="flex items-center space-x-4" data-id="element-1371">
                <div className="relative rounded-md shadow-sm flex-1" data-id="element-1372">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400" data-id="element-1373">
                    $
                  </span>
                  <input type="number" value={priceRange[0]} onChange={e => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])} className="pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="Min" data-id="element-1374" />
                </div>
                <span className="text-gray-500 dark:text-gray-400" data-id="element-1375">to</span>
                <div className="relative rounded-md shadow-sm flex-1" data-id="element-1376">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400" data-id="element-1377">
                    $
                  </span>
                  <input type="number" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value) || 5000])} className="pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="Max" data-id="element-1378" />
                </div>
              </div>
            </div>
            {/* Category Filter */}
            <div data-id="element-1379">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1380">
                Category
              </label>
              <select value={selectedCategory || ''} onChange={e => setSelectedCategory(e.target.value || null)} className="w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-1381">
                <option value="" data-id="element-1382">All Categories</option>
                {categories.map(cat => <option key={cat.id} value={cat.id} data-id="element-1383">
                    {cat.name}
                  </option>)}
              </select>
            </div>
            {/* In Stock Filter */}
            <div data-id="element-1384">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1385">
                Availability
              </label>
              <div className="flex items-center" data-id="element-1386">
                <input id="inStock" type="checkbox" className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded" data-id="element-1387" />
                <label htmlFor="inStock" className="ml-2 text-gray-700 dark:text-gray-300" data-id="element-1388">
                  In Stock Only
                </label>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end" data-id="element-1389">
            <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 mr-2" data-id="element-1390">
              Reset Filters
            </button>
            <button className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600" data-id="element-1391">
              Apply Filters
            </button>
          </div>
        </div>}
      {/* Product Grid */}
      {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-1392">
          {filteredProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-1393" />)}
        </div> : <div className="py-12 text-center" data-id="element-1394">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-1395">
            No products found
          </h3>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1396">
            Try adjusting your search or filter criteria
          </p>
        </div>}
      {/* Pagination */}
      {filteredProducts.length > 0 && <div className="flex justify-center mt-12" data-id="element-1397">
          <nav className="inline-flex rounded-md shadow" data-id="element-1398">
            <button className="px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1399">
              Previous
            </button>
            <button className="px-3 py-2 border-t border-b border-gray-300 dark:border-gray-600 bg-yellow-500 text-white" data-id="element-1400">
              1
            </button>
            <button className="px-3 py-2 border-t border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1401">
              2
            </button>
            <button className="px-3 py-2 border-t border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1402">
              3
            </button>
            <button className="px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1403">
              Next
            </button>
          </nav>
        </div>}
    </div>;
};